import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from '../../../components/ui/context-menu';
import { Check, CheckCheck, File as FileIcon, Languages, CornerUpLeft, Forward, Euro, Zap, Download, Loader2, Trash2, Captions, AlertTriangle, RotateCw, Clock } from 'lucide-react';
import api, { apiError, mediaUrl, fmtTimeParis } from '../../../lib/api';
import { VoiceNote } from './VoiceNote';

const QUICK_REACTIONS = ['❤️', '🔥', '😂', '😍', '👍', '🙏'];

// APERÇU DES LIENS DE PAIEMENT / DONS (04/09, demande Marlon : « voir l'aperçu avec le
// prix comme sur Telegram »). Affiché CÔTÉ CHATHUB seulement — le fan, lui, voit
// l'aperçu généré par Telegram. Une seule requête par URL, partagée par toutes les
// bulles et gardée en mémoire pour la session.
const DROPP_RE = /https?:\/\/[^\s]*dropp(?:\.fans|fans\.com)[^\s]*/i;
const previewCache = new Map();  // url -> Promise<data>

function fetchPreview(url) {
  if (!previewCache.has(url)) {
    previewCache.set(url, api.get('/link-preview', { params: { url } })
      .then(({ data }) => data)
      .catch(() => ({ erreur: true })));
  }
  return previewCache.get(url);
}

function LinkPreview({ url, mine }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    let vivant = true;
    fetchPreview(url).then((d) => { if (vivant) setP(d); });
    return () => { vivant = false; };
  }, [url]);
  if (!p || p.erreur || (!p.titre && !p.prix)) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer"
      className={`mx-3.5 mb-2.5 flex items-center gap-2.5 rounded-xl border p-2 no-underline ${mine ? 'border-white/15 bg-black/20' : 'border-white/10 bg-white/[0.04]'}`}
      data-testid="link-preview">
      {p.image
        ? <img src={p.image} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="w-11 h-11 rounded-lg object-cover shrink-0 bg-white/5" />
        : <div className="w-11 h-11 rounded-lg bg-white/10 shrink-0 flex items-center justify-center text-[15px]">{p.don ? '🎁' : '🔒'}</div>}
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium text-white truncate">{p.titre || (p.don ? 'Don' : 'Contenu')}</div>
        <div className="text-[11px] text-slate-400 truncate">{p.don ? 'don' : p.hote || 'dropp.fans'}</div>
      </div>
      {p.prix && <span className="shrink-0 text-[12px] font-medium text-emerald-300 bg-emerald-500/15 rounded-md px-2 py-0.5">{p.prix}</span>}
    </a>
  );
}

export function MessageBubble({ m, translation, onReportSale, onReply, onTranslate, onMediaLoaded, onDelete, onReacted, onRetry }) {
  const mine = m.from_ === 'me';
  const mediaOnly = m.media && !m.text && m.media.type !== 'voice';
  const timeStr = fmtTimeParis(m.created_at) || m.time;
  const seenStr = m.seen_at;

  const [loadingMedia, setLoadingMedia] = useState(false);
  const [localUrl, setLocalUrl] = useState(null);
  const [transcript, setTranscript] = useState(m.transcript || null);
  const [transcribing, setTranscribing] = useState(false);
  const effectiveUrl = localUrl || m.media?.url;
  const needsDownload = m.media && !effectiveUrl && m.media.type !== 'voice';

  const mediaRef = useRef(null);
  const autoTriedRef = useRef(false);

  const handleDownloadMedia = async () => {
    if (loadingMedia) return;
    setLoadingMedia(true);
    try {
      const { data } = await api.post(`/telegram/messages/${m.id}/download-media`);
      if (data?.url) {
        setLocalUrl(data.url);
        onMediaLoaded && onMediaLoaded(m.id, data.url);
      }
    } catch (e) {
      toast.error('Impossible de charger le média');
    } finally {
      setLoadingMedia(false);
    }
  };

  // Telegram-style: photos & videos load AUTOMATICALLY as soon as they scroll near
  // the viewport (no "Load photo" click). rootMargin pre-loads slightly ahead so the
  // media is usually ready before it's even visible. Documents stay click-to-load
  // (they can be large). Caching means this doesn't re-cost bandwidth on reopen.
  const autoLoadable = needsDownload && (m.media?.type === 'photo' || m.media?.type === 'video');
  useEffect(() => {
    if (!autoLoadable || autoTriedRef.current) return undefined;
    const el = mediaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { autoTriedRef.current = true; handleDownloadMedia(); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting) && !autoTriedRef.current) {
        autoTriedRef.current = true;
        io.disconnect();
        handleDownloadMedia();
      }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadable]);

  const handleTranscribe = async () => {
    if (transcribing || transcript) return;
    setTranscribing(true);
    try {
      const { data } = await api.post(`/telegram/messages/${m.id}/transcribe`);
      setTranscript(data.text);
    } catch (e) {
      toast.error(apiError(e, 'Échec de la transcription'));
    } finally {
      setTranscribing(false);
    }
  };

  // 15/09 (Marlon : « quand je veux mettre un cœur, ça prend un petit temps ») : réaction affichée TOUT DE SUITE (même
  // calcul que le serveur : l'ancienne réaction retirée, la nouvelle ajoutée), envoi en arrière-plan, retour arrière si refus.
  const reactionSeq = useRef(0);
  const handleReact = async (emoji) => {
    const remove = m.my_reaction === emoji;
    const avant = { reactions: m.reactions || [], mine: m.my_reaction || null };
    let liste = (m.reactions || []).map(r => ({ ...r }));
    if (m.my_reaction) liste = liste.map(r => (r.emoji === m.my_reaction ? { ...r, count: (r.count || 1) - 1 } : r)).filter(r => r.count > 0);
    if (!remove) { const r = liste.find(x => x.emoji === emoji); if (r) r.count = (r.count || 0) + 1; else liste.push({ emoji, count: 1 }); }
    const n = ++reactionSeq.current;
    onReacted && onReacted(m.id, liste, remove ? null : emoji);
    try {
      const { data } = await api.post(`/telegram/messages/${m.id}/react`, { emoji: remove ? null : emoji });
      if (n === reactionSeq.current && data && Array.isArray(data.reactions)) onReacted && onReacted(m.id, data.reactions, data.my_reaction || null);
    } catch (e) {
      if (n !== reactionSeq.current) return;
      onReacted && onReacted(m.id, avant.reactions, avant.mine);
      toast.error(apiError(e, 'Réaction pas envoyée'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce message ? Il sera aussi supprimé sur Telegram des deux côtés.')) return;
    try {
      await api.delete(`/messages/${m.id}`);
      onDelete && onDelete(m.id);
      toast.success('Message supprimé');
    } catch (e) {
      toast.error(apiError(e, 'Échec de la suppression'));
    }
  };

  // Render a blurred preview (stripped thumb) until the user clicks to load HD
  const renderLazyPreview = (isVideo) => (
    <button
      type="button"
      ref={mediaRef}
      onClick={handleDownloadMedia}
      data-testid={`media-lazy-${m.id}`}
      className={`relative ${mediaOnly ? 'w-72' : 'w-full'} ${isVideo ? 'h-44' : 'max-h-80'} bg-[#18171C] overflow-hidden block group`}
    >
      {m.media.thumb ? (
        <img src={m.media.thumb} alt="preview" className="w-full h-full object-cover blur-sm scale-105 opacity-90" />
      ) : (
        <div className="w-full h-44 flex items-center justify-center text-slate-500"><FileIcon size={28} /></div>
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/40 group-hover:bg-black/55 transition-colors">
        {loadingMedia ? (
          <Loader2 size={22} className="text-white animate-spin" />
        ) : (
          <>
            <div className="rounded-full bg-white/15 backdrop-blur p-2.5"><Download size={18} className="text-white" /></div>
            <span className="text-[11px] text-white/90 font-medium">
              {m.media.size_mb ? `Charger ${isVideo ? 'la vidéo' : 'la photo'} · ${m.media.size_mb} Mo` : `Charger ${isVideo ? 'la vidéo' : 'la photo'}`}
            </span>
          </>
        )}
      </div>
    </button>
  );

  const mediaBlock = m.media && (
    <div className={`relative overflow-hidden ${mediaOnly ? 'rounded-2xl' : ''}`}>
      {needsDownload && (m.media.type === 'photo' || m.media.type === 'video') ? (
        renderLazyPreview(m.media.type === 'video')
      ) : effectiveUrl && m.media.type === 'photo' ? (
        <img src={mediaUrl(effectiveUrl)} alt={m.media.name} className={`${mediaOnly ? 'w-72' : 'w-full'} max-h-80 object-cover block`} />
      ) : effectiveUrl && m.media.type === 'video' ? (
        <video src={mediaUrl(effectiveUrl)} muted loop playsInline preload="metadata" controls className={`${mediaOnly ? 'w-72' : 'w-full'} h-44 object-cover block`}
          onMouseEnter={e => e.target.play().catch(() => {})} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} />
      ) : effectiveUrl && m.media.type === 'voice' ? (
        <VoiceNote url={mediaUrl(effectiveUrl)} mine={mine} />
      ) : needsDownload ? (
        <button type="button" onClick={handleDownloadMedia} data-testid={`media-lazy-doc-${m.id}`} className="w-64 h-20 bg-[#18171C] hover:bg-[#1E1D24] transition-colors flex items-center justify-center gap-2 text-slate-300 text-xs">
          {loadingMedia ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {loadingMedia ? 'Chargement…' : `Charger ${m.media.name || 'le fichier'}${m.media.size_mb ? ` · ${m.media.size_mb} Mo` : ''}`}
        </button>
      ) : (
        <div className="w-64 h-20 bg-[#18171C] flex items-center justify-center gap-2 text-slate-400 text-xs"><FileIcon size={16} /> {m.media.name}</div>
      )}
      {mediaOnly && (
        <span className="absolute bottom-1.5 right-2 text-[10px] text-white bg-black/55 px-1.5 py-0.5 rounded-md flex items-center gap-1">
          {timeStr}
          {mine && (m.seen_at ? <CheckCheck size={10} className="text-sky-300" /> : <Check size={10} className="opacity-70" />)}
        </span>
      )}
    </div>
  );

  const bubble = mediaOnly ? (
    <div>
      {m.reply_to && (
        <div className="mb-1 px-2 py-1 rounded-lg border-l-2 border-indigo-400 bg-white/5 text-[10px] text-slate-400">
          <span className="font-semibold">↩ {m.reply_to.name}</span> · {m.reply_to.text}
        </div>
      )}
      {mediaBlock}
    </div>
  ) : (
    <div className={`rounded-2xl text-sm leading-relaxed overflow-hidden ${mine ? 'bg-[rgb(var(--a400))] text-white rounded-br-md' : 'bg-[#18171C] text-slate-200 rounded-bl-md'}`}>
      {m.reply_to && (
        <div className={`mx-3 mt-2 px-2 py-1 rounded-lg border-l-2 text-[10px] ${mine ? 'bg-[rgb(var(--a500))]/70 border-indigo-200 text-indigo-100' : 'bg-white/5 border-indigo-400 text-slate-400'}`}>
          <span className="font-semibold">↩ {m.reply_to.name}</span> · {m.reply_to.text}
        </div>
      )}
      {mediaBlock}
      {m.text && <div className="px-3.5 py-2.5 whitespace-pre-wrap">{m.text}</div>}
      {m.text && DROPP_RE.test(m.text) && <LinkPreview url={(m.text.match(DROPP_RE) || [])[0]} mine={mine} />}
      {translation && (
        <div className={`mx-3 mb-1.5 px-2 py-1.5 rounded-lg text-[11px] italic flex items-start gap-1.5 ${mine ? 'bg-[rgb(var(--a500))]/70 text-indigo-100' : 'bg-sky-500/10 text-sky-300 border border-sky-500/20'}`} data-testid={`translation-${m.id}`}>
          <Languages size={11} className="shrink-0 mt-0.5" /> {translation}
        </div>
      )}
      {transcript && (
        <div className={`mx-3 mb-1.5 px-2 py-1.5 rounded-lg text-[11px] italic flex items-start gap-1.5 ${mine ? 'bg-[rgb(var(--a500))]/70 text-indigo-100' : 'bg-violet-500/10 text-violet-300 border border-violet-500/20'}`} data-testid={`transcript-${m.id}`}>
          <Captions size={11} className="shrink-0 mt-0.5" /> {transcript}
        </div>
      )}
      <div className={`flex flex-col items-end gap-0 text-[10px] px-3.5 pb-2 ${mine ? 'text-indigo-100/90' : 'text-slate-500'}`}>
        {mine ? (
          <>
            <span className="flex items-center gap-1">
              {m.has_dropfans_link && <Zap size={9} className="text-amber-300" />}
              {m._failed
                ? (<button onClick={onRetry} data-testid={`retry-${m.id}`} className="flex items-center gap-1 text-rose-300 hover:text-rose-200 font-semibold"><AlertTriangle size={10} /> Non envoyé — toucher pour réessayer <RotateCw size={10} /></button>)
                : m._pending ? (<><Clock size={9} className="opacity-80" /> {timeStr}</>) : <>Envoyé à : {timeStr}</>}
            </span>
            {seenStr && <span className="flex items-center gap-1 text-sky-200"><CheckCheck size={11} /> Lu à : {seenStr}</span>}
          </>
        ) : (
          <span>{timeStr}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex flex-col max-w-[70%] ${mine ? 'items-end' : 'items-start'}`}>
        <ContextMenu>
          <ContextMenuTrigger asChild><div data-testid={`msg-${m.id}`}>{bubble}</div></ContextMenuTrigger>
          <ContextMenuContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
            {m.telegram_message_id && (
              <>
                <div className="flex gap-1 px-2 py-1.5" data-testid={`react-row-${m.id}`}>
                  {QUICK_REACTIONS.map(e => (
                    <button key={e} type="button" onClick={() => handleReact(e)}
                      className={`text-base leading-none rounded-md p-0.5 hover:scale-125 transition-transform ${m.my_reaction === e ? 'bg-indigo-500/30 scale-110' : ''}`}>
                      {e}
                    </button>
                  ))}
                </div>
                <ContextMenuSeparator className="bg-white/5" />
              </>
            )}
            <ContextMenuItem data-testid={`ctx-msg-reply-${m.id}`} onClick={onReply} className="focus:bg-white/5"><CornerUpLeft size={13} className="mr-2" /> Répondre</ContextMenuItem>
            <ContextMenuItem onClick={() => toast.info('Transfert — choisis la conversation cible (bientôt disponible)')} className="focus:bg-white/5"><Forward size={13} className="mr-2" /> Transférer</ContextMenuItem>
            {!mine && m.text && <ContextMenuItem data-testid={`ctx-msg-translate-${m.id}`} onClick={onTranslate} className="focus:bg-white/5"><Languages size={13} className="mr-2 text-sky-400" /> Traduire en français</ContextMenuItem>}
            {m.media?.type === 'voice' && (
              <ContextMenuItem data-testid={`ctx-msg-transcribe-${m.id}`} onClick={handleTranscribe} className="focus:bg-white/5">
                {transcribing ? <Loader2 size={13} className="mr-2 animate-spin" /> : <Captions size={13} className="mr-2 text-violet-400" />} Transcrire le vocal
              </ContextMenuItem>
            )}
            {mine && <ContextMenuSeparator className="bg-white/5" />}
            {mine && <ContextMenuItem data-testid={`ctx-report-sale-${m.id}`} onClick={onReportSale} className="focus:bg-white/5"><Euro size={13} className="mr-2 text-emerald-400" /> Signaler une vente</ContextMenuItem>}
            <ContextMenuSeparator className="bg-white/5" />
            <ContextMenuItem data-testid={`ctx-msg-delete-${m.id}`} onClick={handleDelete} className="focus:bg-white/5 text-rose-400 focus:text-rose-300"><Trash2 size={13} className="mr-2" /> Supprimer le message</ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
        {(m.reactions || []).length > 0 && (
          <div className={`flex gap-1 mt-0.5 flex-wrap ${mine ? 'justify-end' : ''}`} data-testid={`reactions-${m.id}`}>
            {m.reactions.map(r => (
              <button key={r.emoji} type="button" onClick={() => handleReact(r.emoji)}
                className={`text-[11px] px-1.5 py-0.5 rounded-full border flex items-center gap-1 transition-colors ${m.my_reaction === r.emoji ? 'bg-indigo-500/30 border-indigo-400/50 text-white' : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}>
                <span>{r.emoji}</span>{r.count > 1 && <span className="font-semibold">{r.count}</span>}
              </button>
            ))}
          </div>
        )}
        {mine && m.chatter_name && <span className="text-[10px] text-slate-500 mt-0.5 pr-1" data-testid={`msg-employee-${m.id}`}>{m.chatter_name}</span>}
      </div>
    </div>
  );
}
